import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { Eye, EyeSlash, MapPin, Check, SpinnerGap, MagnifyingGlass } from '@phosphor-icons/react';
import { MapPin as MapPinLucide } from 'lucide-react';
import { toast } from 'sonner';

const LanguageToggle = ({ language, toggleLanguage }) => (
  <div className="absolute top-6 right-6 z-10 flex bg-white/80 backdrop-blur-sm border border-[hsl(40,20%,88%)] rounded-full overflow-hidden text-xs shadow-sm" data-testid="auth-lang-toggle">
    <button onClick={() => language !== 'fr' && toggleLanguage()}
      className={`px-3 py-1.5 font-semibold transition-colors ${language === 'fr' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-[var(--ls-text-muted)]'}`}>FR</button>
    <button onClick={() => language !== 'en' && toggleLanguage()}
      className={`px-3 py-1.5 font-semibold transition-colors ${language === 'en' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-[var(--ls-text-muted)]'}`}>EN</button>
  </div>
);

const API = process.env.REACT_APP_BACKEND_URL;

const OrbitRegisterPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const { register } = useOrbitAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [sponsorCode, setSponsorCode] = useState(searchParams.get('ref') || '');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [locationData, setLocationData] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [cityOverride, setCityOverride] = useState('');

  const t = (fr, en) => language === 'fr' ? fr : en;

  const lookupPostalCode = useCallback(async (code) => {
    if (!code || code.length < 3) { setLocationData(null); setLocationError(''); return; }
    setLocationLoading(true);
    setLocationError('');
    try {
      const res = await fetch(`${API}/api/locations/lookup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postal_code: code })
      });
      if (res.ok) {
        const data = await res.json();
        setLocationData(data);
        setCityOverride(data.city || '');
      } else {
        setLocationData(null); setCityOverride('');
        const err = await res.json().catch(() => ({}));
        setLocationError(err.detail || t('Code postal non reconnu', 'Unrecognized postal code'));
      }
    } catch { setLocationError(t('Erreur de connexion', 'Connection error')); setLocationData(null); }
    finally { setLocationLoading(false); }
  }, [t]);

  const handlePostalCodeChange = (e) => {
    let val = e.target.value.toUpperCase();
    setPostalCode(val);
    const clean = val.replace(/\s/g, '');
    if (clean.length >= 5 || (clean.length >= 6 && /^[A-Z]/.test(clean))) { lookupPostalCode(val); }
    else if (clean.length >= 3 && /^[A-Z]\d[A-Z]/.test(clean)) { lookupPostalCode(val); }
    else { setLocationData(null); setLocationError(''); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) { toast.error(t('Les mots de passe ne correspondent pas', 'Passwords do not match')); return; }
    if (password.length < 6) { toast.error(t('6 caracteres minimum', 'Minimum 6 characters')); return; }
    setLoading(true);
    try {
      await register(name, email, password, sponsorCode || null, language, true);
      navigate('/dashboard');
      toast.success(t('Inscription reussie !', 'Registration successful!'));
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] placeholder:text-[var(--ls-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/30 focus:border-[var(--ls-terracotta)] transition-all";

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 bg-[var(--ls-sand)] relative">
        <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
        <div className="w-full max-w-md space-y-6">
          {/* Logo */}
          <div className="text-center lg:text-left">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <MapPinLucide className="w-8 h-8 text-[var(--ls-terracotta)]" />
              <span className="font-heading text-2xl font-bold text-[var(--ls-text)]">
                Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club
              </span>
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--ls-text)] tracking-tight">
              {t('Rejoignez le mouvement !', 'Join the movement!')}
            </h1>
            <p className="mt-2 text-[var(--ls-text-muted)]">
              {t('Creez votre espace membre LocalSite.Club', 'Create your LocalSite.Club member account')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="orbit-register-form">
            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Nom complet', 'Full name')}</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required
                className={inputClass} data-testid="orbit-register-name"
                placeholder={t('Jean Tremblay', 'John Smith')} />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Adresse courriel', 'Email address')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className={inputClass} data-testid="orbit-register-email"
                placeholder={t('votre@courriel.com', 'your@email.com')} />
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                <MapPin className="w-4 h-4 inline-block mr-1 -mt-0.5 text-[var(--ls-terracotta)]" />
                {t('Code postal', 'Postal / ZIP code')}
              </label>
              <div className="relative">
                <input type="text" value={postalCode} onChange={handlePostalCodeChange}
                  placeholder={t('Ex: H2X 1Y4 ou 10001', 'Ex: H2X 1Y4 or 10001')}
                  className={`${inputClass} pr-10`} data-testid="orbit-register-postal-code" />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {locationLoading && <SpinnerGap className="w-5 h-5 text-[var(--ls-terracotta)] animate-spin" />}
                  {locationData && !locationLoading && <Check className="w-5 h-5 text-[var(--ls-success)]" weight="bold" />}
                  {!locationData && !locationLoading && postalCode.length >= 3 && !locationError && <MagnifyingGlass className="w-5 h-5 text-[var(--ls-text-muted)]" />}
                </div>
              </div>
              {locationError && <p className="text-red-500 text-xs mt-1.5" data-testid="postal-code-error">{locationError}</p>}
            </div>

            {/* Location result */}
            {locationData && (
              <div className="bg-[var(--ls-forest)]/5 border border-[var(--ls-forest)]/20 rounded-xl p-4 space-y-3" data-testid="location-result">
                <div className="flex items-center gap-2 text-sm text-[var(--ls-forest)]">
                  <MapPin className="w-4 h-4" weight="fill" />
                  {t('Localisation detectee', 'Location detected')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Pays', 'Country')}</span>
                    <span className="text-sm text-[var(--ls-text)] font-medium" data-testid="detected-country">
                      {language === 'fr' ? locationData.country_name_fr : locationData.country_name_en}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Province / Etat', 'Province / State')}</span>
                    <span className="text-sm text-[var(--ls-text)] font-medium" data-testid="detected-province">
                      {language === 'fr' ? locationData.province_name_fr : locationData.province_name_en}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Ville', 'City')}</label>
                  <input type="text" value={cityOverride} onChange={e => setCityOverride(e.target.value)}
                    placeholder={t('Entrez votre ville', 'Enter your city')}
                    className="w-full px-3 py-2 rounded-lg border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] text-sm focus:outline-none focus:border-[var(--ls-terracotta)] transition-colors"
                    data-testid="city-input" />
                  {locationData.city && cityOverride !== locationData.city && (
                    <button type="button" onClick={() => setCityOverride(locationData.city)}
                      className="text-xs text-[var(--ls-terracotta)] hover:text-[var(--ls-terracotta-dark)] mt-1">
                      {t('Suggestion :', 'Suggested:')} {locationData.city}
                    </button>
                  )}
                </div>
                {locationData.neighborhood && (
                  <div>
                    <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Quartier', 'Neighborhood')}</span>
                    <span className="text-sm text-[var(--ls-text)] font-medium" data-testid="detected-neighborhood">{locationData.neighborhood}</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Mot de passe', 'Password')}</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className={`${inputClass} pr-12`} data-testid="orbit-register-password" placeholder="********" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ls-text-muted)] hover:text-[var(--ls-text)]">
                  {showPw ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Confirmer', 'Confirm')}</label>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
                className={inputClass} data-testid="orbit-register-confirm-password" placeholder="********" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">{t('Code parrain (optionnel)', 'Sponsor code (optional)')}</label>
              <input type="text" value={sponsorCode} onChange={e => setSponsorCode(e.target.value)}
                className={inputClass} placeholder="ORBXXXXXXXX" data-testid="orbit-register-sponsor" />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3.5 rounded-full bg-[var(--ls-terracotta)] text-white font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors disabled:opacity-50 shadow-lg shadow-[var(--ls-terracotta)]/20"
              data-testid="orbit-register-submit">
              {loading ? t('Inscription...', 'Signing up...') : t("S'inscrire gratuitement", 'Sign up for free')}
            </button>

            <p className="text-center text-sm text-[var(--ls-text-muted)]">
              {t('Deja membre ?', 'Already a member?')}
              <Link to="/login" className="text-[var(--ls-terracotta)] hover:text-[var(--ls-terracotta-dark)] ml-1 font-semibold">
                {t('Se connecter', 'Sign in')}
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right — Visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/987d762f-6280-4d2f-beee-1ba5be49cef1/images/cf3ce17456f3599f7b66774ca5e98ed2d49a95335c88f4cffdf5e6f84b99a28f.png"
          alt="Local artisan market"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ls-bark)]/80 via-[var(--ls-bark)]/30 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-3">
            {t("Faisons-nous des amis !", "Let's make friends!")}
          </h2>
          <p className="text-white/80 text-lg max-w-md">
            {t(
              "Decouvrez les artisans, les restaurants et les entreprises qui font vivre votre quartier.",
              "Discover the artisans, restaurants and businesses that bring your neighborhood to life."
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrbitRegisterPage;
